// ./src/utils/api/TaskService.ts
//
// Task / appointment service (event preparation & run-of-show, with server-tracked
// time). OpResult-native, event-scoped: every list is filtered by eventId. Full
// single-item CRUD, a bulk status change / remove (array of ids in the where
// clause), and a `transition` action (start | pause | complete | reopen) that
// accumulates tracked time server-side. See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Enum-like constants (mirror the eventsmagic-api boundary/task.ts)
// =============================================================================

export const TASK_TYPE = {
  TASK: "task",
  APPOINTMENT: "appointment",
} as const;

export const TASK_TYPE_VALUES = Object.values(TASK_TYPE);

export const TASK_STATUS = {
  TODO: "todo",
  IN_PROGRESS: "in_progress",
  PAUSED: "paused",
  DONE: "done",
} as const;

export const TASK_STATUS_VALUES = Object.values(TASK_STATUS);

export const TASK_RESPONSIBLE_KIND = {
  USER: "user",
  CREW: "crew",
  GUEST: "guest",
  VENDOR: "vendor",
  VENUE: "venue",
} as const;

export const TASK_RESPONSIBLE_KIND_VALUES = Object.values(TASK_RESPONSIBLE_KIND);

export const TASK_LOCATION_KIND = {
  VENUE: "venue",
  ADDRESS: "address",
  ONLINE: "online",
} as const;

export const TASK_LOCATION_KIND_VALUES = Object.values(TASK_LOCATION_KIND);

export const TASK_TRANSITION = {
  START: "start",
  PAUSE: "pause",
  COMPLETE: "complete",
  REOPEN: "reopen",
} as const;

export type TaskTransitionAction =
  (typeof TASK_TRANSITION)[keyof typeof TASK_TRANSITION];

// =============================================================================
// GraphQL
// =============================================================================

const TASK_FIELDS = `
  id
  eventId
  accountId
  categoryId
  taskType
  title
  description
  taskStatus
  scheduledAt
  expectedDurationMin
  actualDurationSec
  startedAt
  activeSince
  pausedAt
  completedAt
  responsibleKind
  responsibleId
  responsibleLabel
  locationKind
  locationVenueId
  locationAddress
  latitude
  longitude
  onlineMeetingUrl
  locationLabel
  orderNo
  status
  rights
  createdAt
  updatedAt
`;

const TASK_LIST_QUERY = gql`
  query TaskList($filter: TaskFilter, $params: PaginationAndSorting) {
    taskList(filter: $filter, params: $params) {
      code
      data { ${TASK_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const TASK_GET_QUERY = gql`
  query TaskGet($id: ID) {
    taskGet(id: $id) {
      code
      data { ${TASK_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const TASK_GET_MANY_QUERY = gql`
  query TaskGetMany($ids: [ID]) {
    taskGetMany(ids: $ids) {
      code
      data { ${TASK_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const TASK_CREATE_MUTATION = gql`
  mutation TaskCreate($params: TaskInput) {
    taskCreate(params: $params) {
      code
      data { ${TASK_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const TASK_UPDATE_MUTATION = gql`
  mutation TaskUpdate($where: TaskWhereInput, $params: TaskInput) {
    taskUpdate(where: $where, params: $params) {
      code
      data { ${TASK_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const TASK_REMOVE_MUTATION = gql`
  mutation TaskRemove($where: TaskWhereInput) {
    taskRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

const TASK_TRANSITION_MUTATION = gql`
  mutation TaskTransition($where: TaskWhereInput, $params: TaskTransitionInput) {
    taskTransition(where: $where, params: $params) {
      code
      data { ${TASK_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api Task schema)
// =============================================================================

export interface Task {
  id?: string;
  eventId?: string | null;
  accountId?: string | null;
  categoryId?: string | null;
  taskType?: string | null;
  title?: string | null;
  description?: string | null;
  taskStatus?: string | null;
  scheduledAt?: string | null;
  expectedDurationMin?: number | null;
  actualDurationSec?: number | null;
  startedAt?: string | null;
  activeSince?: string | null;
  pausedAt?: string | null;
  completedAt?: string | null;
  responsibleKind?: string | null;
  responsibleId?: string | null;
  responsibleLabel?: string | null;
  locationKind?: string | null;
  locationVenueId?: string | null;
  locationAddress?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  onlineMeetingUrl?: string | null;
  locationLabel?: string | null;
  orderNo?: number | null;
  status?: number;
  /** Actions the current caller may perform ('edit' | 'remove' | 'duplicate'). */
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface TaskInput {
  eventId?: string | null;
  categoryId?: string | null;
  taskType?: string | null;
  title?: string | null;
  description?: string | null;
  scheduledAt?: string | null;
  expectedDurationMin?: number | null;
  responsibleKind?: string | null;
  responsibleId?: string | null;
  locationKind?: string | null;
  locationVenueId?: string | null;
  locationAddress?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  onlineMeetingUrl?: string | null;
  orderNo?: number | null;
  status?: number | null;
}

// A bulk update (>1 id) may mass-set the responsible party, a due date/time, or a
// category across the selection (server-enforced).
export interface TaskBulkInput {
  categoryId?: string | null;
  scheduledAt?: string | null;
  responsibleKind?: string | null;
  responsibleId?: string | null;
}

export interface TaskFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  categoryId?: string[] | null;
  taskType?: string[] | null;
  taskStatus?: string[] | null;
  responsibleKind?: string[] | null;
  responsibleId?: string[] | null;
  status?: number[] | null;
  searchKeyword?: string | null;
}

export interface TaskWhereInput {
  /** One id, or many for a bulk update/remove/transition. */
  id?: string | string[];
}

// =============================================================================
// Service
// =============================================================================

export class TaskService {
  static async list(
    filter?: TaskFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(TASK_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(TASK_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(TASK_GET_MANY_QUERY, { ids });
  }

  static async create(params: TaskInput): Promise<OpResult> {
    return ApiService.executeMutation(TASK_CREATE_MUTATION, { params });
  }

  static async update(where: TaskWhereInput, params: TaskInput): Promise<OpResult> {
    return ApiService.executeMutation(TASK_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(TASK_REMOVE_MUTATION, { where: { id } });
  }

  /** Walk a task's lifecycle (start | pause | complete | reopen); the server accumulates tracked time. */
  static async transition(id: string, action: TaskTransitionAction): Promise<OpResult> {
    return ApiService.executeMutation(TASK_TRANSITION_MUTATION, {
      where: { id },
      params: { action },
    });
  }

  /** Mass-change status on every selected task in one call (server restricts a multi-id update to status). */
  static async bulkUpdate(ids: string[], params: TaskBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(TASK_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  /** Remove every selected task in one call. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(TASK_REMOVE_MUTATION, { where: { id: ids } });
  }
}
